import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LangContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import api from "../lib/api";
import { CalendarDays, Wallet, Building2, Home, ArrowRight, ArrowLeft } from "lucide-react";

export default function Landing() {
  const { t, dir } = useLang();
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  useEffect(() => {
    // Fire-and-forget visit tracking
    api.post("/track/visit").catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 font-semibold text-[#2A5948]">
            <Home size={20} />
            <span className="text-lg">{t("brand")}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/login"
              data-testid="header-login-link"
              className="hidden sm:inline-flex px-3 py-2 rounded-lg text-sm border border-[#E5E1D8] bg-white/70 hover:bg-white"
            >
              {t("login")}
            </Link>
            <Link
              to="/register"
              data-testid="header-register-link"
              className="inline-flex px-3 py-2 rounded-lg text-sm brand-btn"
            >
              {t("register")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1623680904963-5580d963e18e')",
          }}
        />
        <div className="absolute inset-0 hero-mask" />
        <div className="relative max-w-7xl mx-auto px-4 py-24 sm:py-32 lg:py-40 text-white">
          <div className="max-w-3xl">
            <div className="inline-block px-3 py-1 rounded-full bg-white/15 text-xs tracking-wide backdrop-blur-sm border border-white/20 mb-6">
              {t("trusted_by")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight">
              {t("hero_title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/85 max-w-2xl">
              {t("hero_sub")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                data-testid="hero-register-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl brand-btn font-medium"
              >
                {t("hero_cta")}
                <Arrow size={18} />
              </Link>
              <Link
                to="/login"
                data-testid="hero-login-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 border border-white/30 text-white hover:bg-white/20 font-medium"
              >
                {t("hero_cta_secondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        <FeatureCard icon={CalendarDays} title={t("feature_calendar")} desc={t("feature_calendar_d")} testid="feature-calendar">
          <div className="mt-4 grid grid-cols-7 gap-1">
            {Array.from({ length: 21 }).map((_, i) => {
              const cls = i % 5 === 0 ? "cal-rented" : i % 4 === 0 ? "cal-reserved" : "cal-empty";
              return <div key={i} className={`h-6 rounded ${cls} border border-[#E5E1D8]`} />;
            })}
          </div>
        </FeatureCard>
        <FeatureCard icon={Wallet} title={t("feature_finance")} desc={t("feature_finance_d")} testid="feature-finance">
          <div className="mt-4 flex items-end gap-2 h-24">
            {[40, 65, 30, 80, 55, 90, 70].map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-[#2A5948]" style={{ height: `${h}%` }} />
            ))}
          </div>
        </FeatureCard>
        <FeatureCard icon={Building2} title={t("feature_property")} desc={t("feature_property_d")} testid="feature-property">
          <div className="mt-4 flex gap-3">
            <div className="flex-1 h-24 rounded-lg bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1691520486119-3cff18780ae1')" }} />
            <div className="flex-1 h-24 rounded-lg bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/31817157/pexels-photo-31817157.jpeg')" }} />
          </div>
        </FeatureCard>
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-24">
        <div className="bg-white border border-[#E5E1D8] rounded-3xl p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#1C1E17]">{t("hero_cta")}</h2>
            <p className="mt-2 text-[#5C6053]">{t("hero_sub")}</p>
          </div>
          <Link
            to="/register"
            data-testid="cta-register-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl brand-btn font-medium"
          >
            {t("register")}
            <Arrow size={18} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E1D8] py-8 text-center text-sm text-[#5C6053]">
        © {new Date().getFullYear()} {t("brand")}
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, testid, children }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1D8] rounded-2xl p-6 shadow-sm hover:-translate-y-0.5 transition-transform">
      <div className="w-11 h-11 rounded-xl bg-[#F3EFE7] text-[#2A5948] flex items-center justify-center">
        <Icon size={22} />
      </div>
      <h3 className="mt-4 text-xl font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-[#5C6053]">{desc}</p>
      {children}
    </div>
  );
}
